import React, { useEffect, useState, useCallback } from 'react';
import { api } from '../../utils/api';
import { toast } from 'sonner';
import { Card } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import { Layers, Search, Plus, AlertTriangle, DollarSign, Users, Lock, CheckCircle2, Loader2 } from 'lucide-react';

const emptyForm = { name: '', display_title: '', description: '', package_tier: 'premium_activations',
  aliases: '', qualifications: '', search_tags: '', is_active: true, bookable: false };

const listToStr = (a) => (Array.isArray(a) ? a.join(', ') : '');
const strToList = (s) => (s || '').split(',').map((x) => x.trim()).filter(Boolean);

export default function AdminRoleManagement() {
  const [tiers, setTiers] = useState([]);
  const [activeTier, setActiveTier] = useState(null);
  const [roles, setRoles] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(false);
  const [duplicates, setDuplicates] = useState([]);
  const [pricing, setPricing] = useState([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const loadTiers = useCallback(async () => {
    try {
      const { data } = await api.get('/admin/role-management/tiers');
      setTiers(data.tiers);
      if (!activeTier && data.tiers.length) setActiveTier(data.tiers[0].key);
    } catch (e) { toast.error('Failed to load tiers'); }
  }, [activeTier]);

  const loadRoles = useCallback(async (tier, query) => {
    if (!tier) return;
    setLoading(true);
    try {
      const { data } = await api.get('/admin/role-management/roles', { params: { tier, q: query || undefined } });
      setRoles(data.roles);
    } catch (e) { toast.error('Failed to load roles'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadTiers(); }, [loadTiers]);
  useEffect(() => { if (activeTier) loadRoles(activeTier, q); /* eslint-disable-next-line */ }, [activeTier]);

  const loadDuplicates = async () => {
    try { const { data } = await api.get('/admin/role-management/duplicates'); setDuplicates(data.clusters); }
    catch (e) { toast.error('Failed to load duplicates'); }
  };
  const loadPricing = async () => {
    try { const { data } = await api.get('/admin/role-management/pricing-review'); setPricing(data.by_tier); }
    catch (e) { toast.error('Failed to load pricing review'); }
  };

  const openCreate = () => { setEditing(null); setForm({ ...emptyForm, package_tier: activeTier || 'premium_activations' }); setDialogOpen(true); };
  const openEdit = (r) => {
    setEditing(r);
    setForm({ name: r.name, display_title: r.display_title || '', description: r.description || '',
      package_tier: r.package_tier, aliases: listToStr(r.aliases), qualifications: listToStr(r.qualifications),
      search_tags: listToStr(r.search_tags), is_active: r.is_active, bookable: r.bookable });
    setDialogOpen(true);
  };

  const save = async () => {
    setSaving(true);
    try {
      const payload = { display_title: form.display_title || form.name, description: form.description,
        package_tier: form.package_tier, aliases: strToList(form.aliases),
        qualifications: strToList(form.qualifications), search_tags: strToList(form.search_tags),
        is_active: form.is_active, bookable: form.bookable };
      if (editing) {
        await api.patch(`/admin/role-management/roles/${editing.id}`, payload);
        toast.success('Role updated');
      } else {
        await api.post('/admin/role-management/roles', { name: form.name, ...payload, bookable: false });
        toast.success('Role created (not bookable until priced)');
      }
      setDialogOpen(false);
      await loadTiers(); await loadRoles(activeTier, q);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Save failed'));
    } finally { setSaving(false); }
  };

  const toggleBookable = async (r) => {
    try {
      await api.post('/admin/role-management/bulk-activate', { role_ids: [r.id], bookable: !r.bookable });
      toast.success(!r.bookable ? 'Role is now bookable' : 'Role set to not bookable');
      await loadTiers(); await loadRoles(activeTier, q);
    } catch (e) {
      const d = e?.response?.data?.detail;
      toast.error(typeof d === 'string' ? d : (d?.message || 'Could not change bookability'));
    }
  };

  return (
    <div className="max-w-7xl mx-auto px-6 py-8" data-testid="admin-role-management">
      <div className="flex items-center gap-3 mb-2">
        <Layers className="h-6 w-6 text-cyan-600" />
        <h1 className="text-2xl font-bold">Role Management</h1>
      </div>
      <p className="text-sm text-muted-foreground mb-6">Eight Package Tiers · staffing directory · aliases · qualifications · pricing review. New roles stay <strong>not bookable</strong> until their pricing is approved. Existing roles remain operational.</p>

      <Tabs defaultValue="directory">
        <TabsList data-testid="rm-tabs">
          <TabsTrigger value="directory" data-testid="rm-tab-directory">Directory</TabsTrigger>
          <TabsTrigger value="duplicates" onClick={loadDuplicates} data-testid="rm-tab-duplicates">Duplicate Review</TabsTrigger>
          <TabsTrigger value="pricing" onClick={loadPricing} data-testid="rm-tab-pricing">Pricing Review</TabsTrigger>
        </TabsList>

        {/* DIRECTORY */}
        <TabsContent value="directory">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 my-4" data-testid="rm-tier-cards">
            {tiers.map((t) => (
              <button key={t.key} onClick={() => setActiveTier(t.key)}
                data-testid={`rm-tier-${t.key}`}
                className={`text-left p-3 rounded-lg border transition-colors ${activeTier === t.key ? 'border-cyan-500 bg-cyan-50' : 'border-gray-200 hover:border-gray-300'}`}>
                <div className="text-sm font-semibold">{t.label}</div>
                <div className="text-xs text-muted-foreground mt-1">{t.total} roles · {t.bookable} bookable
                  {t.pending_pricing > 0 && <span className="text-amber-600"> · {t.pending_pricing} pending</span>}</div>
                <div className="text-[10px] text-gray-400 mt-0.5">billing: {t.billing_fold}</div>
              </button>
            ))}
          </div>

          <div className="flex items-center gap-3 mb-4">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-gray-400" />
              <Input value={q} onChange={(e) => setQ(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && loadRoles(activeTier, q)}
                placeholder="Search name, alias or tag…" className="pl-8" data-testid="rm-search" />
            </div>
            <Button variant="outline" onClick={() => loadRoles(activeTier, q)} data-testid="rm-search-btn">Search</Button>
            <Button onClick={openCreate} data-testid="rm-add-role-btn"><Plus className="h-4 w-4 mr-1" /> Add Role</Button>
          </div>

          {loading ? <div className="flex justify-center py-10"><Loader2 className="h-6 w-6 animate-spin text-cyan-500" /></div> : (
            <div className="space-y-2" data-testid="rm-role-list">
              {roles.map((r) => (
                <Card key={r.id} className="p-3 flex items-center justify-between" data-testid={`rm-role-${r.id}`}>
                  <div className="min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span className="font-medium">{r.display_title}</span>
                      {r.bookable
                        ? <Badge className="bg-green-100 text-green-700" data-testid={`rm-bookable-${r.id}`}><CheckCircle2 className="h-3 w-3 mr-1" />Bookable</Badge>
                        : <Badge className="bg-amber-100 text-amber-700"><Lock className="h-3 w-3 mr-1" />Pending pricing</Badge>}
                      {r.has_rate_config && <Badge variant="outline" className="text-[10px]"><DollarSign className="h-3 w-3 mr-0.5" />Rate set</Badge>}
                      {!r.is_active && <Badge variant="outline" className="text-gray-400">Inactive</Badge>}
                      {r.in_use > 0 && <Badge variant="outline" className="text-[10px]" title="References across bookings/talent/pricing"><Users className="h-3 w-3 mr-0.5" />{r.in_use} in use</Badge>}
                    </div>
                    {(r.aliases?.length > 0) && <div className="text-xs text-muted-foreground mt-0.5 truncate">aliases: {r.aliases.join(', ')}</div>}
                    {(r.qualifications?.length > 0) && <div className="text-[11px] text-indigo-500 mt-0.5 truncate">quals: {r.qualifications.join(', ')}</div>}
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <Button size="sm" variant="ghost" onClick={() => toggleBookable(r)} data-testid={`rm-toggle-bookable-${r.id}`}>{r.bookable ? 'Make not bookable' : 'Make bookable'}</Button>
                    <Button size="sm" variant="outline" onClick={() => openEdit(r)} data-testid={`rm-edit-${r.id}`}>Edit</Button>
                  </div>
                </Card>
              ))}
              {roles.length === 0 && <p className="text-sm text-muted-foreground py-6 text-center">No roles in this tier/search.</p>}
            </div>
          )}
        </TabsContent>

        {/* DUPLICATES */}
        <TabsContent value="duplicates">
          <div className="my-4 space-y-3" data-testid="rm-duplicates">
            <div className="flex items-center gap-2 text-amber-700 text-sm"><AlertTriangle className="h-4 w-4" /> Potential duplicates (exact-title variants). No merges run automatically — review in-use counts, then alias or keep distinct.</div>
            {duplicates.map((c, i) => (
              <Card key={i} className="p-3" data-testid={`rm-dup-${i}`}>
                <div className="text-xs uppercase text-gray-400 mb-2">{c.type}</div>
                <div className="space-y-1">
                  {c.members.map((m) => (
                    <div key={m.id} className="flex items-center justify-between text-sm">
                      <span>{m.name} <span className="text-gray-400">({m.package_tier})</span></span>
                      <span className="text-xs">{m.in_use} refs {m.bookable ? '· bookable' : '· pending'}</span>
                    </div>
                  ))}
                </div>
              </Card>
            ))}
            {duplicates.length === 0 && <p className="text-sm text-muted-foreground">Load the tab to scan. None found means no exact-title collisions.</p>}
          </div>
        </TabsContent>

        {/* PRICING REVIEW */}
        <TabsContent value="pricing">
          <div className="my-4 space-y-4" data-testid="rm-pricing-review">
            <p className="text-sm text-muted-foreground">Roles needing pricing before they can go bookable. Configure a rate (existing pricing tools), then make bookable.</p>
            {pricing.map((grp) => {
              const pending = grp.roles.filter((r) => r.needs_pricing);
              return (
                <Card key={grp.key} className="p-3" data-testid={`rm-pricing-${grp.key}`}>
                  <div className="font-semibold text-sm mb-2">{grp.label} <span className="text-xs text-amber-600">· {pending.length} need pricing</span></div>
                  <div className="flex flex-wrap gap-2">
                    {grp.roles.map((r) => (
                      <Badge key={r.id} variant="outline" className={r.needs_pricing ? 'text-amber-700' : 'text-green-700'}>
                        {r.name} {r.has_rate_config ? '✓' : '—'}
                      </Badge>
                    ))}
                  </div>
                </Card>
              );
            })}
          </div>
        </TabsContent>
      </Tabs>

      {/* CREATE / EDIT DIALOG */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="rm-dialog">
          <DialogHeader><DialogTitle>{editing ? 'Edit Role' : 'Add Role'}</DialogTitle></DialogHeader>
          <div className="space-y-3">
            {!editing && (
              <div>
                <label className="text-xs font-medium">Canonical name *</label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rm-form-name" />
              </div>
            )}
            <div>
              <label className="text-xs font-medium">Display title</label>
              <Input value={form.display_title} onChange={(e) => setForm({ ...form, display_title: e.target.value })} data-testid="rm-form-display" />
            </div>
            <div>
              <label className="text-xs font-medium">Package Tier</label>
              <select className="w-full border rounded-md h-9 px-2 text-sm" value={form.package_tier}
                onChange={(e) => setForm({ ...form, package_tier: e.target.value })} data-testid="rm-form-tier">
                {tiers.map((t) => <option key={t.key} value={t.key}>{t.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-medium">Description</label>
              <Input value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="rm-form-desc" />
            </div>
            <div>
              <label className="text-xs font-medium">Aliases (comma-separated, searchable)</label>
              <Input value={form.aliases} onChange={(e) => setForm({ ...form, aliases: e.target.value })} data-testid="rm-form-aliases" />
            </div>
            <div>
              <label className="text-xs font-medium">Qualifications / licenses (comma-separated)</label>
              <Input value={form.qualifications} onChange={(e) => setForm({ ...form, qualifications: e.target.value })} data-testid="rm-form-quals" />
            </div>
            <div>
              <label className="text-xs font-medium">Search tags / specialties (comma-separated)</label>
              <Input value={form.search_tags} onChange={(e) => setForm({ ...form, search_tags: e.target.value })} data-testid="rm-form-tags" />
            </div>
            {editing && (
              <div className="flex items-center gap-4 text-sm">
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.is_active} onChange={(e) => setForm({ ...form, is_active: e.target.checked })} data-testid="rm-form-active" /> Active</label>
                <label className="flex items-center gap-2"><input type="checkbox" checked={form.bookable} onChange={(e) => setForm({ ...form, bookable: e.target.checked })} data-testid="rm-form-bookable" /> Bookable (needs rate)</label>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} data-testid="rm-cancel">Cancel</Button>
            <Button onClick={save} disabled={saving || (!editing && !form.name.trim())} data-testid="rm-save">{saving ? 'Saving…' : 'Save'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
